import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReelCard from '../components/feed/ReelCard';
import { fetchReels, recordReelView, toggleReelLike } from '../services/training';
import { useAuth } from '../context/AuthContext';
import { createFeedShuffleSeed, pickRandomFeedSort } from '../utils/reelFeedSort';

const PAGE_SIZE = 8;

function createFeedLoadConfig() {
  const sort = pickRandomFeedSort();
  return {
    sort,
    shuffleSeed: sort === 'random' ? createFeedShuffleSeed() : null,
  };
}

export default function FeedPage() {
  const { user } = useAuth();
  const feedConfigRef = useRef(null);
  if (!feedConfigRef.current) {
    feedConfigRef.current = createFeedLoadConfig();
  }
  const { sort, shuffleSeed } = feedConfigRef.current;

  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const viewedReels = useRef(new Set());

  useEffect(() => {
    const unlockAudio = () => setAudioUnlocked(true);
    document.addEventListener('pointerdown', unlockAudio, { once: true, passive: true });
    document.addEventListener('keydown', unlockAudio, { once: true });
    return () => {
      document.removeEventListener('pointerdown', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
    };
  }, []);

  const loadReels = useCallback(async (offset = 0, replace = true) => {
    const batch = await fetchReels({
      sort,
      shuffleSeed,
      limit: PAGE_SIZE,
      offset,
      userId: user?.id,
    });
    setHasMore(batch.length === PAGE_SIZE);
    setReels((prev) => (replace ? batch : [...prev, ...batch]));
    return batch;
  }, [shuffleSeed, sort, user?.id]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    loadReels(0, true)
      .catch(console.error)
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [loadReels]);

  const handleLoadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    loadReels(reels.length, false)
      .catch(console.error)
      .finally(() => setLoadingMore(false));
  }, [hasMore, loadReels, loadingMore, reels.length]);

  const handleReelVisible = useCallback((reelId) => {
    if (viewedReels.current.has(reelId)) return;
    viewedReels.current.add(reelId);
    recordReelView(reelId).catch(console.error);
  }, []);

  const handleLike = useCallback(async (reelId, currentlyLiked) => {
    const liked = await toggleReelLike(reelId, currentlyLiked);
    setReels((prev) =>
      prev.map((reel) => {
        if (reel.id !== reelId) return reel;
        const delta = liked ? 1 : -1;
        return {
          ...reel,
          likedByMe: liked,
          likes: Math.max(0, reel.likes + delta),
        };
      })
    );
    return liked;
  }, []);

  if (loading) {
    return (
      <div className="h-full w-full bg-slate-950 flex items-center justify-center text-white/60 text-sm font-bold uppercase tracking-widest">
        Cargando clips…
      </div>
    );
  }

  if (reels.length === 0) {
    return (
      <div className="h-full w-full bg-slate-950 flex flex-col items-center justify-center gap-4 text-white/70 px-8 text-center">
        <p className="text-lg font-black uppercase tracking-tight">Aún no hay reels</p>
        <p className="text-sm text-white/50 max-w-md">
          Los instructores publicarán clips cortos aquí. Vuelve pronto.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 w-full bg-slate-950 overflow-y-scroll overscroll-y-contain snap-y snap-mandatory hide-scrollbar relative">
      {reels.map((reel, index) => (
        <ReelCard
          key={reel.id}
          reel={reel}
          isLiked={reel.likedByMe}
          audioUnlocked={audioUnlocked}
          onLike={() => handleLike(reel.id, reel.likedByMe)}
          onVisible={() => handleReelVisible(reel.id)}
          isLast={index === reels.length - 1}
          onNearEnd={handleLoadMore}
        />
      ))}

      {loadingMore && (
        <div className="py-8 text-center text-white/50 text-xs font-bold uppercase tracking-widest">
          Cargando más…
        </div>
      )}
    </div>
  );
}
